/**
 * Iter91.7 — Pie chart de ingresos O egresos por categoría.
 * `mode="expense"` (default) agrupa solo `type=expense` por `category`.
 * `mode="income"` agrupa solo `type=income` por `category`.
 * Se usa dos veces en FinancePage para el stack vertical derecho.
 */
import React, { useMemo } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { CATEGORY_LABELS } from "./FinancePage.utils";
import { PieChart as PieIcon, TrendingUp } from "lucide-react";

const COLORS_EXPENSE = ["#ff7351", "#ffd16c", "#a855f7", "#3b82f6", "#10b981",
                       "#f59e0b", "#ec4899", "#06b6d4", "#eab308", "#6b7280"];
const COLORS_INCOME  = ["#10b981", "#34d399", "#6ee7b7", "#a7f3d0", "#ffd16c",
                       "#3b82f6", "#a855f7", "#06b6d4", "#eab308", "#6b7280"];

function fmt(v) {
  if (v >= 1000) return `$${(v / 1000).toFixed(1)}k`;
  return `$${Math.round(v)}`;
}

export default function ExpensePie({ entries = [], currency = "UYU", mode = "expense", className = "" }) {
  const isIncome = mode === "income";
  const COLORS = isIncome ? COLORS_INCOME : COLORS_EXPENSE;
  const iconColor = isIncome ? "#10b981" : "#ff7351";
  const headingColor = isIncome ? "rgba(16,185,129,0.12)" : "rgba(255,115,81,0.12)";
  const borderColor = isIncome ? "rgba(16,185,129,0.25)" : "rgba(255,115,81,0.25)";
  const Icon = isIncome ? TrendingUp : PieIcon;
  const title = isIncome ? "Ingresos por categoría" : "Gastos por categoría";

  const data = useMemo(() => {
    const byCat = {};
    entries.forEach(e => {
      if (isIncome && e.type !== "income") return;
      if (!isIncome && e.type !== "expense") return;
      const k = e.category || "general";
      byCat[k] = (byCat[k] || 0) + (Number(e.amount) || 0);
    });
    return Object.entries(byCat)
      .map(([cat, amount]) => ({
        name: CATEGORY_LABELS[cat] || cat,
        value: Math.round(amount * 100) / 100,
      }))
      .sort((a, b) => b.value - a.value);
  }, [entries, isIncome]);

  const total = data.reduce((s, d) => s + d.value, 0);

  return (
    <div
      className={`rounded-3xl p-4 sm:p-5 flex flex-col ${className}`}
      style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.07)" }}
      data-testid={isIncome ? "income-pie-card" : "expense-pie-card"}
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
             style={{ background: headingColor, border: `1px solid ${borderColor}` }}>
          <Icon size={13} style={{ color: iconColor }} />
        </div>
        <div className="min-w-0">
          <h3 className="text-sm font-black text-white tracking-tight leading-tight"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {title}
          </h3>
          <p className="text-[9.5px] text-gray-500 font-bold uppercase tracking-widest">
            Total: {fmt(total)} {currency}
          </p>
        </div>
      </div>

      {data.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-[11px] text-gray-600 italic py-6">
          {isIncome ? "Sin ingresos registrados" : "Sin egresos registrados"}
        </div>
      ) : (
        <>
          <div className="h-28 -mx-2" data-testid={isIncome ? "income-pie-chart" : "expense-pie-chart"}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey="value" nameKey="name"
                     cx="50%" cy="50%" innerRadius={28} outerRadius={54}
                     paddingAngle={2} stroke="none">
                  {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: "#0e0e0e", border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 12, fontSize: 11, fontWeight: 700, color: "#fff",
                  }}
                  formatter={(v) => [fmt(v), ""]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="space-y-1 mt-2 overflow-y-auto pr-1" style={{ maxHeight: 88 }}>
            {data.slice(0, 5).map((d, i) => {
              const pct = total > 0 ? (d.value / total * 100) : 0;
              return (
                <div key={d.name} className="flex items-center gap-1.5 text-[10px]">
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                        style={{ background: COLORS[i % COLORS.length] }} />
                  <span className="font-bold text-gray-200 truncate flex-1" title={d.name}>{d.name}</span>
                  <span className="font-mono text-gray-500 tabular-nums text-[9.5px]">{pct.toFixed(0)}%</span>
                </div>
              );
            })}
            {data.length > 5 && (
              <p className="text-[9px] text-gray-600 font-bold italic pt-0.5">
                + {data.length - 5} categorías más
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
